import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Cpu, TrendingUp, Lock, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

const COPY = {
  en: {
    overline: "Subscription — Step 1 of 2",
    title: "Choose your Titan Units.",
    lead:
      "Slide to select the number of Titan Units you wish to subscribe. Once you validate, our Investor Relations team is notified and will send you your personalised contract by email.",
    unitsLabel: "Titan Units",
    amountLabel: "Amount",
    yieldLabel: "Indicative annual yield range",
    yieldAverage: "Average target",
    yieldRange: "15 % → 30 % / year",
    yieldAvgLabel: "22 % / year",
    proj: "Projected returns (indicative)",
    projAnnual: "Annual yield",
    projMonthly: "Monthly yield",
    proj5y: "5-year cumulative",
    disclaimer:
      "Indicative figures based on our operational range. Not a guaranteed return. Detailed terms are defined in the subscription contract you will sign.",
    cta: "Validate my subscription",
    confirmTitle: "Confirm your subscription",
    confirmBody:
      "You are about to validate a subscription of {units} Titan Units for a total of {amount}. This choice will be locked and communicated to our Investor Relations team.",
    confirmCta: "Yes, validate",
    cancel: "Cancel",
    submitted: "Subscription submitted",
    lockedTitle: "Your subscription is under review",
    lockedBody:
      "Our Investor Relations team has been notified and will send you your personalised subscription contract by email at {email}. Meanwhile, you can start uploading your KYC documents.",
    lockedAmountLabel: "Subscribed amount",
    lockedNext: "Next step: KYC documents",
    lockedNextCta: "Upload my KYC documents",
  },
  fr: {
    overline: "Souscription — Étape 1 sur 2",
    title: "Choisissez vos Titan Units.",
    lead:
      "Faites glisser le curseur pour sélectionner le nombre de Titan Units que vous souhaitez souscrire. Une fois votre choix validé, notre équipe Investor Relations est notifiée et vous enverra votre contrat personnalisé par email.",
    unitsLabel: "Titan Units",
    amountLabel: "Montant",
    yieldLabel: "Fourchette de rendement annuel indicatif",
    yieldAverage: "Objectif moyen",
    yieldRange: "15 % → 30 % / an",
    yieldAvgLabel: "22 % / an",
    proj: "Projection de rendement (indicatif)",
    projAnnual: "Rendement annuel",
    projMonthly: "Rendement mensuel",
    proj5y: "Cumulé sur 5 ans",
    disclaimer:
      "Chiffres indicatifs basés sur notre plage opérationnelle. Pas un rendement garanti. Les termes détaillés sont définis dans le contrat de souscription que vous signerez.",
    cta: "Valider ma souscription",
    confirmTitle: "Confirmer votre souscription",
    confirmBody:
      "Vous êtes sur le point de valider une souscription de {units} Titan Units pour un total de {amount}. Ce choix sera verrouillé et communiqué à notre équipe Investor Relations.",
    confirmCta: "Oui, valider",
    cancel: "Annuler",
    submitted: "Souscription envoyée",
    lockedTitle: "Votre souscription est en cours de traitement",
    lockedBody:
      "Notre équipe Investor Relations a été notifiée et vous enverra votre contrat de souscription personnalisé par email à l'adresse {email}. En attendant, vous pouvez commencer à déposer vos documents KYC.",
    lockedAmountLabel: "Montant souscrit",
    lockedNext: "Prochaine étape : documents KYC",
    lockedNextCta: "Déposer mes documents KYC",
  },
  es: {
    overline: "Suscripción — Paso 1 de 2",
    title: "Elige tus Titan Units.",
    lead:
      "Desliza el cursor para seleccionar el número de Titan Units que deseas suscribir. Una vez validada tu elección, nuestro equipo de Investor Relations es notificado y te enviará tu contrato personalizado por email.",
    unitsLabel: "Titan Units",
    amountLabel: "Importe",
    yieldLabel: "Rango de rendimiento anual indicativo",
    yieldAverage: "Objetivo medio",
    yieldRange: "15 % → 30 % / año",
    yieldAvgLabel: "22 % / año",
    proj: "Proyección de rendimiento (indicativa)",
    projAnnual: "Rendimiento anual",
    projMonthly: "Rendimiento mensual",
    proj5y: "Acumulado 5 años",
    disclaimer:
      "Cifras indicativas basadas en nuestro rango operativo. No es un rendimiento garantizado. Los términos detallados se definen en el contrato de suscripción que firmarás.",
    cta: "Validar mi suscripción",
    confirmTitle: "Confirmar tu suscripción",
    confirmBody:
      "Estás a punto de validar una suscripción de {units} Titan Units por un total de {amount}. Esta elección quedará bloqueada y comunicada a nuestro equipo de Investor Relations.",
    confirmCta: "Sí, validar",
    cancel: "Cancelar",
    submitted: "Suscripción enviada",
    lockedTitle: "Tu suscripción está en revisión",
    lockedBody:
      "Nuestro equipo de Investor Relations ha sido notificado y te enviará tu contrato de suscripción personalizado por email a {email}. Mientras tanto, puedes empezar a subir tus documentos KYC.",
    lockedAmountLabel: "Importe suscrito",
    lockedNext: "Próximo paso: documentos KYC",
    lockedNextCta: "Subir mis documentos KYC",
  },
};

const fmtUSD = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtNum = (n) => new Intl.NumberFormat("en-US").format(n);

export default function SubscriptionChoice({ onSubmitted }) {
  const { api, token, user } = useAuth();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;

  const [choice, setChoice] = useState(null);   // {status, titan_units, amount_usd, config}
  const [units, setUnits] = useState(500);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${api}/me/subscription-choice`, { headers: h })
      .then(r => {
        setChoice(r.data);
        if (r.data.status === "pending") setUnits(500);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, token]);

  const cfg = choice?.config || { price_per_unit_usd: 40, min_units: 250, max_units: 2500, step: 50, yield_low: 15, yield_avg: 22, yield_high: 30 };
  const amount = useMemo(() => units * cfg.price_per_unit_usd, [units, cfg.price_per_unit_usd]);
  const yearlyLow = Math.round(amount * cfg.yield_low / 100);
  const yearlyAvg = Math.round(amount * cfg.yield_avg / 100);
  const yearlyHigh = Math.round(amount * cfg.yield_high / 100);
  const monthlyAvg = Math.round(yearlyAvg / 12);
  const fiveYearAvg = Math.round(amount * (Math.pow(1 + cfg.yield_avg / 100, 5) - 1));

  const sliderPct = ((units - cfg.min_units) / (cfg.max_units - cfg.min_units)) * 100;

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${api}/me/subscription-choice`, { titan_units: units }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(c.submitted, { duration: 4000 });
      const r = await axios.get(`${api}/me/subscription-choice`, { headers: { Authorization: `Bearer ${token}` } });
      setChoice(r.data);
      setConfirming(false);
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="subscription-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
      </div>
    );
  }

  // ─────────── LOCKED STATE (submitted) ───────────
  if (choice?.status === "submitted") {
    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="subscription-locked">
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-8 md:p-12">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="overline text-emerald-400">{c.submitted}</p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-light tracking-tighter">{c.lockedTitle}</h2>
              <p className="mt-6 text-muted-foreground leading-relaxed max-w-2xl">
                {c.lockedBody.replace("{email}", user?.email || "")}
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-lg">
                <div className="border border-border p-5 bg-card/40">
                  <p className="overline text-muted-foreground">{c.unitsLabel}</p>
                  <p className="mt-2 font-display text-4xl tracking-tighter" data-testid="locked-units">{fmtNum(choice.titan_units)}</p>
                </div>
                <div className="border border-border p-5 bg-card/40">
                  <p className="overline text-muted-foreground">{c.lockedAmountLabel}</p>
                  <p className="mt-2 font-display text-4xl tracking-tighter" data-testid="locked-amount">{fmtUSD(choice.amount_usd)}</p>
                </div>
              </div>

              <div className="mt-10 flex items-center gap-2 text-[11px] tracking-widest uppercase text-muted-foreground">
                <Lock className="w-3.5 h-3.5" /> Locked
              </div>
            </div>
          </div>
        </div>

        {/* Next step: KYC */}
        <a
          href="/portal/documents"
          data-testid="locked-kyc-cta"
          className="mt-6 group block rounded-2xl border border-sky-500/30 bg-sky-500/[0.06] p-6 md:p-7 hover:bg-sky-500/[0.10] transition-colors"
        >
          <p className="overline text-sky-400">{c.lockedNext}</p>
          <p className="mt-2 font-display text-xl tracking-tight">{c.lockedNextCta}</p>
        </a>
      </motion.div>
    );
  }

  // ─────────── WIZARD (pending) ───────────
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="subscription-wizard">
      <p className="overline text-emerald-400">{c.overline}</p>
      <h1 className="mt-3 font-display text-3xl md:text-5xl font-light tracking-tighter leading-[1.05]">{c.title}</h1>
      <p className="mt-6 text-muted-foreground leading-relaxed max-w-2xl">{c.lead}</p>

      {/* Big value display */}
      <div className="mt-12 rounded-2xl border border-border bg-gradient-to-br from-emerald-500/[0.06] via-transparent to-sky-500/[0.06] p-8 md:p-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
          <div>
            <p className="overline text-muted-foreground">{c.unitsLabel}</p>
            <p className="mt-2 flex items-baseline gap-3">
              <span data-testid="slider-units" className="font-display font-light tracking-tighter text-6xl md:text-7xl leading-none">
                {fmtNum(units)}
              </span>
            </p>
          </div>
          <div className="md:text-right">
            <p className="overline text-muted-foreground">{c.amountLabel}</p>
            <p className="mt-2 font-display font-light tracking-tighter text-6xl md:text-7xl leading-none" data-testid="slider-amount">
              {fmtUSD(amount)}
            </p>
          </div>
        </div>

        {/* Slider */}
        <div className="mt-12">
          <input
            type="range"
            min={cfg.min_units}
            max={cfg.max_units}
            step={cfg.step}
            value={units}
            onChange={(e) => setUnits(Number(e.target.value))}
            data-testid="units-slider"
            aria-label="Titan Units"
            className="w-full h-2 rounded-full appearance-none cursor-pointer bg-secondary accent-emerald-500 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:shadow-lg [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-black"
            style={{ background: `linear-gradient(to right, rgb(52 211 153) 0%, rgb(52 211 153) ${sliderPct}%, hsl(var(--secondary)) ${sliderPct}%, hsl(var(--secondary)) 100%)` }}
          />
          <div className="mt-3 flex justify-between text-[11px] tracking-widest uppercase text-muted-foreground">
            <span>{fmtNum(cfg.min_units)} · {fmtUSD(cfg.min_units * cfg.price_per_unit_usd)}</span>
            <span>{fmtNum(cfg.max_units)} · {fmtUSD(cfg.max_units * cfg.price_per_unit_usd)}</span>
          </div>
        </div>
      </div>

      {/* Yield indicator */}
      <div className="mt-8 rounded-2xl border border-border bg-card/40 p-6 md:p-7" data-testid="yield-summary">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          <p className="overline text-emerald-400">{c.yieldLabel}</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
          <p className="font-display text-2xl md:text-3xl tracking-tight">{c.yieldRange}</p>
          <p className="text-[13px] text-muted-foreground">
            {c.yieldAverage} — <span className="text-emerald-400 font-medium">{c.yieldAvgLabel}</span>
          </p>
        </div>
      </div>

      {/* Projections grid */}
      <div className="mt-10">
        <p className="overline text-muted-foreground mb-4">{c.proj}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ProjCard testId="proj-annual" label={c.projAnnual} main={fmtUSD(yearlyAvg)} sub={`${fmtUSD(yearlyLow)} → ${fmtUSD(yearlyHigh)}`} />
          <ProjCard testId="proj-monthly" label={c.projMonthly} main={fmtUSD(monthlyAvg)} sub="~22 % / 12" />
          <ProjCard testId="proj-5y" label={c.proj5y} main={fmtUSD(fiveYearAvg)} sub="Compounded @ 22 %" />
        </div>
      </div>

      {/* Disclaimer + CTA */}
      <div className="mt-10 flex items-start gap-3 text-[12px] leading-relaxed text-muted-foreground max-w-3xl">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>{c.disclaimer}</p>
      </div>

      <div className="mt-10">
        <button
          onClick={() => setConfirming(true)}
          data-testid="subscription-validate-btn"
          className="inline-flex items-center gap-2 h-12 md:h-14 px-8 rounded-full bg-gradient-to-r from-emerald-500 to-sky-500 hover:from-emerald-400 hover:to-sky-400 text-white text-[13px] tracking-wide font-medium transition-colors"
        >
          <Cpu className="w-4 h-4" /> {c.cta}
        </button>
      </div>

      {/* Confirmation modal */}
      {confirming && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !submitting && setConfirming(false)}>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-card border border-border rounded-2xl max-w-lg w-full p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="confirm-modal"
          >
            <h3 className="font-display text-2xl tracking-tight">{c.confirmTitle}</h3>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {c.confirmBody.replace("{units}", fmtNum(units)).replace("{amount}", fmtUSD(amount))}
            </p>
            <div className="mt-8 flex items-center gap-3 justify-end">
              <button
                onClick={() => setConfirming(false)}
                disabled={submitting}
                data-testid="confirm-cancel"
                className="h-11 px-5 rounded-full border border-border text-[13px] hover:bg-secondary/40 transition-colors disabled:opacity-50"
              >
                {c.cancel}
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="confirm-submit"
                className="h-11 px-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-[13px] font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {c.confirmCta}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}

const ProjCard = ({ label, main, sub, testId }) => (
  <div data-testid={testId} className="border border-border p-5 bg-card/40 rounded-lg">
    <p className="overline text-muted-foreground">{label}</p>
    <p className="mt-3 font-display text-2xl tracking-tight">{main}</p>
    <p className="mt-1.5 text-[11px] text-muted-foreground">{sub}</p>
  </div>
);
